import React, { useState, useEffect, useMemo } from "react";
import {
  MDBIcon,
  MDBInputGroup,
  MDBCol,
  MDBRow,
  MDBSpinner,
} from "mdb-react-ui-kit";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";

import "../scss/datatable.scss";

const VoteTable = ({ votes, legislatorDetails }) => {
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    party: { value: null, matchMode: FilterMatchMode.CONTAINS },
    district: { value: null, matchMode: FilterMatchMode.CONTAINS },
    voteTypeText: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [loading, setLoading] = useState(true);

  const voteTableData = useMemo(() => {
    let data = [];

    const addVoteRows = (voteType, legislatorIds) => {
      legislatorIds.forEach((legislatorId) => {
        const legislator = legislatorDetails[legislatorId];
        if (legislator) {
          const chamberPrefix = legislator.chamber === "HOUSE" ? "HD" : "SD";
          const isExcused = voteType.toLowerCase().includes('excused') || voteType.toLowerCase().includes('_by_proxy') ;
          const voteTypeText = `${voteType.toLowerCase().replace('_excused', '').replace('_by_proxy', '')} ${isExcused ? " *" : ""}`; // Plain text for filtering

          data.push({
            name: `${legislator.lastName}, ${legislator.firstName}`,
            party: legislator.politicalParty.name,
            district: `${chamberPrefix} ${legislator.district.number}`,
            voteTypeText,
            voteType: (
              <>
                {voteType.replace('_excused', '').replace('_by_proxy', '')} {isExcused && <strong>*</strong>}
              </>
            ),
          });
        }
      });
    };

    // Handle all vote types including excused
    Object.entries(votes).forEach(([voteType, legislatorIds]) => {
      if (['yes', 'yes_excused', 'no', 'no_excused', 'absent', 'yes_by_proxy', 'no_by_proxy', 'no_vote'].includes(voteType.toLowerCase())) {
        addVoteRows(voteType, legislatorIds);
      }
    });

    setLoading(false);
    return data;
  }, [votes, legislatorDetails]);

  //Spinner
  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <MDBSpinner role="status" size="lg">
          <span className="visually-hidden">Loading...</span>
        </MDBSpinner>
      </div>
    );
  }

  //Filters
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setFilters((prevFilters) => ({
      ...prevFilters,
      global: { ...prevFilters.global, value },
    }));
  };

  return (
    <>
      <MDBRow className="g-1">
        <MDBCol size="lg">
          <MDBInputGroup
            className="mb-3"
            noBorder
            textBefore={<MDBIcon fas icon="search" />}
          >
            <input
              className="form-control"
              type="text"
              placeholder="Search"
              value={filters.global.value || ""}
              onChange={onGlobalFilterChange}
            />
          </MDBInputGroup>
        </MDBCol>
      </MDBRow>
      <DataTable
        value={voteTableData}
        paginator
        rows={100}
        rowsPerPageOptions={[25, 50, 100]}
        scrollable
        scrollHeight="580px"
        sortField="name"
        sortOrder={1}
        removableSort
        loading={loading}
        filters={filters}
        globalFilterFields={["name", "party", "district", "voteTypeText"]}
      >
        <Column field="name" header="Legislator" sortable filter />
        <Column field="party" header="Party" sortable filter />
        <Column field="district" header="District" sortable filter />
        <Column field="voteTypeText" header="Vote" sortable filter body={(rowData) => rowData.voteType} />
      </DataTable>
    </>
  );
};

export default VoteTable;
